/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  fetchAdvisoryMappingAccess,
  fetchAuthzContext,
  fetchBillingAccess,
  fetchRolePermissions,
} from '../api/authorization'
import type { AuthzContextResponse } from '../api/authorization'
import { useAuthSession } from '../lib/session'
import { ApiRequestError } from '../lib/httpClient'
import type { AuthSession } from '../features/auth/types'
import type { AdvicePrepPaywall } from '../features/adviceprep/types'
import {
  isAdvicePrepPaywallReason,
  isExemptFromAdvicePrepPaywall,
} from '../features/adviceprep/paywall/constants'
import { onAdvicePrepPaymentRequired } from '../features/adviceprep/paywall/paymentRequired'
import { ClientTypes, type PermissionKey, permissions, UserTypes } from '../types/authorization'

type AuthorizationState = {
  clientId: string
  permissions: Set<PermissionKey>
  advicePrepPaywall: AdvicePrepPaywall | null
  // Server-side flags from the API's FEATURE_FLAGS CodeBuild variable. Empty outside authz-context mode.
  featureFlags: Record<string, boolean>
  isLoading: boolean
  isReady: boolean
  error: string | null
  hasPermission: (permission: PermissionKey) => boolean
  hasAnyPermission: (required: PermissionKey[]) => boolean
  hasAllPermissions: (required: PermissionKey[]) => boolean
  refresh: () => Promise<void>
}

function readAdvicePrepPaywall(context: AuthzContextResponse): AdvicePrepPaywall | null {
  const paywall = context.constraints?.advisePrepPaywall
  if (!paywall?.active || !paywall.reason) return null
  if (!isAdvicePrepPaywallReason(paywall.reason)) return null
  return { active: true, reason: paywall.reason, since: paywall.since ?? null }
}

const zeroGuid = '00000000-0000-0000-0000-000000000000'
const permissionValues = new Set<PermissionKey>(Object.values(permissions))
const useLegacyAuthz = String(import.meta.env['VITE_USE_LEGACY_AUTHZ'] ?? 'true').toLowerCase() === 'true'
const useAuthzContext = String(import.meta.env['VITE_USE_AUTHZ_CONTEXT'] ?? 'false').toLowerCase() === 'true'
const useAuthzContextOnly =
  String(import.meta.env['VITE_USE_AUTHZ_CONTEXT_ONLY'] ?? 'false').toLowerCase() === 'true'
const cacheVersion = 'v4'
const cacheTtlMs = Number(import.meta.env['VITE_AUTHZ_CACHE_TTL_MS'] ?? 300000)
const cachePrefix = 'authz'
const authzMode = useLegacyAuthz ? 'legacy' : useAuthzContext ? 'authz-context' : 'roles'

const AuthorizationContext = createContext<AuthorizationState | null>(null)

type CacheKeyParts = {
  userId: string
  userTypeId: number
  clientTypeId: number
  clientId: string
}

type PermissionCacheValue = {
  permissions: PermissionKey[]
  cachedAt: number
  version: string
}

function buildPermissionCacheKey(parts: CacheKeyParts) {
  return `${cachePrefix}:${cacheVersion}:${authzMode}:${parts.userId}:${parts.userTypeId}:${parts.clientTypeId}:${parts.clientId}`
}

function getPermissionCacheKey(session: AuthSession, clientId: string) {
  return buildPermissionCacheKey({
    userId: session.currentUserId || '',
    userTypeId: session.currentUserTypeId || 0,
    clientTypeId: session.defaultClientTypeId ?? session.currentClientTypeId ?? 0,
    clientId,
  })
}

function sanitizePermissions(list: string[]) {
  return list.filter((role): role is PermissionKey => permissionValues.has(role as PermissionKey))
}

function readPermissionCache(session: AuthSession, clientId: string): Set<PermissionKey> | null {
  try {
    const raw = localStorage.getItem(getPermissionCacheKey(session, clientId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as PermissionCacheValue
    if (
      !Array.isArray(parsed?.permissions) ||
      typeof parsed?.cachedAt !== 'number' ||
      parsed?.version !== cacheVersion
    ) {
      return null
    }
    if (Date.now() - parsed.cachedAt > cacheTtlMs) return null
    return new Set(parsed.permissions.filter((item): item is PermissionKey => permissionValues.has(item)))
  } catch {
    return null
  }
}

function writePermissionCache(session: AuthSession, clientId: string, list: PermissionKey[]) {
  const payload: PermissionCacheValue = {
    permissions: list,
    cachedAt: Date.now(),
    version: cacheVersion,
  }
  localStorage.setItem(getPermissionCacheKey(session, clientId), JSON.stringify(payload))
}

function clearPermissionCache(session: AuthSession, clientId: string) {
  localStorage.removeItem(getPermissionCacheKey(session, clientId))
}

function clearPermissionCacheForParts(parts: CacheKeyParts) {
  localStorage.removeItem(buildPermissionCacheKey(parts))
}

/**
 * Signup calls this before creating an account, because from that point the
 * browser is a different user. Anything cached from a previous session would
 * otherwise pre-fill the provider with someone else's permissions.
 */
export function clearCachedPermissions() {
  clearAllPermissionCaches()
}

function clearAllPermissionCaches() {
  for (let i = localStorage.length - 1; i >= 0; i -= 1) {
    const key = localStorage.key(i)
    if (!key?.startsWith(`${cachePrefix}:`)) continue
    localStorage.removeItem(key)
  }
}

function deriveExtraPermissions(
  session: AuthSession,
  clientId: string,
  hasAdvisoryAccess: boolean,
  hasBillingAccess: boolean
): PermissionKey[] {
  const extra = new Set<PermissionKey>()
  const defaultClientId = session.defaultClientId || session.currentClientId || zeroGuid
  const baseClientTypeId = session.defaultClientTypeId ?? session.currentClientTypeId

  if (baseClientTypeId === ClientTypes.Restricted) {
    if (session.currentUserTypeId === UserTypes.Admin) {
      extra.add(permissions.viewClient)
      extra.add(permissions.viewAdminUser)
    }
  }

  if (
    baseClientTypeId === ClientTypes.Advisor &&
    session.currentUserTypeId === UserTypes.ClientUser
  ) {
    extra.add(permissions.viewClient)
    if (defaultClientId && defaultClientId !== clientId) {
      extra.add(permissions.viewClientChild)
      extra.add(permissions.viewPlatform)
      extra.add(permissions.viewUser)
      extra.add(permissions.viewUserGroup)
      extra.add(permissions.viewReport)
      extra.add(permissions.editReport)
      extra.add(permissions.addPlatform)
      extra.add(permissions.addUser)
      extra.add(permissions.addUserGroup)
      extra.add(permissions.editPlatform)
      extra.add(permissions.editUser)
      extra.add(permissions.editUserGroup)
    }
  }

  if (
    baseClientTypeId === ClientTypes.Standard &&
    session.currentUserTypeId === UserTypes.ClientAdmin
  ) {
    extra.add(permissions.viewClientChild)
  }

  if (
    hasAdvisoryAccess &&
    (session.currentUserTypeId === UserTypes.ClientAdmin ||
      session.currentUserTypeId === UserTypes.ClientUser ||
      session.currentUserTypeId === UserTypes.Admin)
  ) {
    extra.add(permissions.viewAdvisoryClient)
    extra.add(permissions.viewClientChild)
  }

  if (
    hasBillingAccess &&
    (session.currentUserTypeId === UserTypes.ClientAdmin ||
      session.currentUserTypeId === UserTypes.Admin)
  ) {
    extra.add(permissions.viewBilling)
  }

  if (session.currentUserTypeId === UserTypes.Admin) {
    extra.add(permissions.manageReportOptions)
  }

  return Array.from(extra)
}

export function AuthorizationProvider({ children }: { children: JSX.Element }) {
  const session = useAuthSession()
  const [permissionSet, setPermissionSet] = useState<Set<PermissionKey>>(new Set())
  const [advicePrepPaywall, setAdvicePrepPaywall] = useState<AdvicePrepPaywall | null>(null)
  const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>({})
  const hasSessionContext = Boolean(session?.idToken && session.currentClientTypeId)
  const [isLoading, setIsLoading] = useState(hasSessionContext)
  const [isReady, setIsReady] = useState(!hasSessionContext)
  const [error, setError] = useState<string | null>(null)
  const loadIdRef = useRef(0)
  const previousSessionRef = useRef<CacheKeyParts | null>(null)
  // Flags are global on the API, so they are only taken from a load on a new idToken (login or
  // token refresh). Client switches and other session updates keep the flags already applied.
  const flagsTokenRef = useRef<string | null>(null)
  // loadPermissions reads the session through this ref so that session updates unrelated to
  // permissions (e.g. defaultPageSize) don't trigger a reload.
  const sessionRef = useRef(session)
  sessionRef.current = session

  useEffect(() => {
    const nextSession: CacheKeyParts | null =
      session?.idToken && session.currentClientTypeId
        ? {
            userId: session.currentUserId || '',
            userTypeId: session.currentUserTypeId || 0,
            clientTypeId: session.defaultClientTypeId ?? session.currentClientTypeId ?? 0,
            clientId: session.currentClientId || zeroGuid,
          }
        : null

    const previousSession = previousSessionRef.current

    if (!nextSession) {
      clearAllPermissionCaches()
      previousSessionRef.current = null
      flagsTokenRef.current = null
      setPermissionSet(new Set())
      setAdvicePrepPaywall(null)
      setFeatureFlags({})
      setError(null)
      setIsLoading(false)
      setIsReady(true)
      return
    }

    if (previousSession && previousSession.userId && previousSession.userId !== nextSession.userId) {
      clearAllPermissionCaches()
    } else if (
      previousSession &&
      previousSession.userId === nextSession.userId &&
      previousSession.clientId !== nextSession.clientId
    ) {
      clearPermissionCacheForParts(previousSession)
    }

    previousSessionRef.current = nextSession
  }, [
    session?.idToken,
    session?.currentClientId,
    session?.currentClientTypeId,
    session?.defaultClientTypeId,
    session?.currentUserId,
    session?.currentUserTypeId,
  ])

  const loadPermissions = useCallback(async (forceRefresh = false) => {
    const loadId = ++loadIdRef.current
    const activeSession = sessionRef.current

    if (!activeSession?.idToken || !activeSession.currentClientTypeId) {
      if (loadIdRef.current === loadId) {
        flagsTokenRef.current = null
        setPermissionSet(new Set())
        setAdvicePrepPaywall(null)
        setFeatureFlags({})
        setError(null)
        setIsLoading(false)
        setIsReady(true)
      }
      return
    }

    const clientId = activeSession.currentClientId || zeroGuid
    const idToken = activeSession.idToken
    const shouldLoadFlags = flagsTokenRef.current !== idToken
    if (!forceRefresh) {
      const cached = readPermissionCache(activeSession, clientId)
      if (cached && loadIdRef.current === loadId) {
        setPermissionSet(cached)
      }
    }

    if (loadIdRef.current === loadId) {
      setIsLoading(true)
      setIsReady(false)
      setError(null)
    }

    try {
      // Only the authz-context branch can produce a paywall. Reset it up front
      // so the other branches — and a client switch — cannot inherit a stale
      // one, in either direction.
      let paywall: AdvicePrepPaywall | null = null
      let flags: Record<string, boolean> = {}
      let list: PermissionKey[] = []
      if (useLegacyAuthz) {
        const [roles, hasAdvisoryAccess, hasBilling] = await Promise.all([
          fetchRolePermissions(activeSession.currentClientTypeId, clientId),
          fetchAdvisoryMappingAccess(clientId),
          fetchBillingAccess(clientId),
        ])
        const typedRoles = sanitizePermissions(roles)
        const merged = new Set<PermissionKey>([
          ...typedRoles,
          ...deriveExtraPermissions(activeSession, clientId, hasAdvisoryAccess, hasBilling),
        ])
        list = Array.from(merged)
      } else if (useAuthzContext) {
        try {
          const [context, hasAdvisoryAccess, hasBilling] = await Promise.all([
            fetchAuthzContext(clientId),
            fetchAdvisoryMappingAccess(clientId),
            fetchBillingAccess(clientId),
          ])
          const fromContext = new Set(sanitizePermissions(context.permissions || []))
          const extras = deriveExtraPermissions(activeSession, clientId, hasAdvisoryAccess, hasBilling)
          extras.forEach((p) => fromContext.add(p))
          list = Array.from(fromContext)
          paywall = readAdvicePrepPaywall(context)
          flags = context.constraints?.featureFlags ?? {}
        } catch (e) {
          if (useAuthzContextOnly || !(e instanceof ApiRequestError) || e.status !== 404) {
            throw e
          }
          const [roles, hasAdvisoryAccess, hasBilling] = await Promise.all([
            fetchRolePermissions(activeSession.currentClientTypeId, clientId),
            fetchAdvisoryMappingAccess(clientId),
            fetchBillingAccess(clientId),
          ])
          const merged = new Set<PermissionKey>([
            ...sanitizePermissions(roles),
            ...deriveExtraPermissions(activeSession, clientId, hasAdvisoryAccess, hasBilling),
          ])
          list = Array.from(merged)
        }
      } else {
        const roles = await fetchRolePermissions(activeSession.currentClientTypeId, clientId)
        list = Array.from(new Set(sanitizePermissions(roles)))
      }

      if (loadIdRef.current !== loadId) return
      writePermissionCache(activeSession, clientId, list)
      setPermissionSet(new Set(list))
      setAdvicePrepPaywall(paywall)
      if (shouldLoadFlags) {
        flagsTokenRef.current = idToken
        setFeatureFlags(flags)
      }
    } catch (e) {
      if (loadIdRef.current !== loadId) return
      clearPermissionCache(activeSession, clientId)
      setPermissionSet(new Set())
      setAdvicePrepPaywall(null)
      // Keep the flags already applied for this token; a failed load on a new token leaves
      // flagsTokenRef unchanged, so the next load fetches them again.
      setError(e instanceof Error ? e.message : 'Unable to load permissions')
    } finally {
      if (loadIdRef.current === loadId) {
        setIsLoading(false)
        setIsReady(true)
      }
    }
  }, [])

  // Only the fields the load depends on, so unrelated session updates don't reload.
  useEffect(() => {
    void loadPermissions()
  }, [
    loadPermissions,
    session?.idToken,
    session?.currentClientId,
    session?.currentClientTypeId,
    session?.defaultClientId,
    session?.defaultClientTypeId,
    session?.currentUserId,
    session?.currentUserTypeId,
  ])

  // A 402 carries the same reason the context would have, so it can raise the
  // paywall directly. The next context load re-derives it, which is what clears
  // it again once the subscription is paid.
  useEffect(() => {
    onAdvicePrepPaymentRequired((reason) => {
      setAdvicePrepPaywall({ active: true, reason, since: null })
    })
    return () => onAdvicePrepPaymentRequired(null)
  }, [])

  const currentUserTypeId = session?.currentUserTypeId
  const value = useMemo<AuthorizationState>(
    () => ({
      clientId: session?.currentClientId || '',
      permissions: permissionSet,
      advicePrepPaywall:
        advicePrepPaywall && isExemptFromAdvicePrepPaywall(currentUserTypeId, advicePrepPaywall.reason)
          ? null
          : advicePrepPaywall,
      featureFlags,
      isLoading,
      isReady,
      error,
      hasPermission: (permission) => permissionSet.has(permission),
      hasAnyPermission: (required) => required.some((perm) => permissionSet.has(perm)),
      hasAllPermissions: (required) => required.every((perm) => permissionSet.has(perm)),
      refresh: async () => loadPermissions(true),
    }),
    [
      session?.currentClientId,
      currentUserTypeId,
      permissionSet,
      advicePrepPaywall,
      featureFlags,
      isLoading,
      isReady,
      error,
      loadPermissions,
    ]
  )

  return <AuthorizationContext.Provider value={value}>{children}</AuthorizationContext.Provider>
}

export function useAuthorization() {
  const context = useContext(AuthorizationContext)
  if (!context) {
    throw new Error('useAuthorization must be used within AuthorizationProvider')
  }
  return context
}

export function useCan(permission: PermissionKey) {
  return useAuthorization().hasPermission(permission)
}
